import { Fragment, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts'
import { ChartLine, FlaskConical, Plus, TriangleAlert } from 'lucide-react'
import { api } from '../../lib/api'
import { Carte, Chargement, EtatVide, Message } from '../base'
import VisionneuseDocument from '../VisionneuseDocument'
import FormulaireResultat from './FormulaireResultat'
import * as f from '../../lib/format'

export type Cellule = {
  donneeId: string
  valeurSi: number | null
  uniteSi: string | null
  valeurOriginale: number | null
  uniteOriginale: string | null
  comparateur: string | null
  valeurTexte: string | null
  refBasse: number | null
  refHaute: number | null
  refOriginale: { basse: number | null; haute: number | null } | null
  anormal: boolean | null
  conversionFacteur: number | null
  conversionNote: string | null
  conversionIncertaine: boolean
  laboratoire: string | null
  documentId: string | null
  documentTitre: string | null
  page: number | null
  passage: string | null
}

type Biologie = {
  colonnes: {
    id: string
    datePrelevement: string
    heure: string | null
    laboratoire: string | null
    documentId: string | null
    nbResultats: number
  }[]
  lignes: {
    code: string | null
    libelle: string
    categorie: string
    uniteCible: string | null
    connu: boolean
    cellules: Record<string, Cellule>
  }[]
  alertes: { id: string; donneeId: string | null; severite: string; libelle: string }[]
}

/**
 * Arrondi d'affichage. La valeur exacte reste en base : une créatinine à
 * 63,648 µmol/L s'affiche 63,6, comme sur un compte rendu, sans que le chiffre
 * enregistré change.
 */
export function nombreLisible(valeur: number): string {
  const amplitude = Math.abs(valeur)
  const decimales = amplitude >= 100 ? 0 : amplitude >= 10 ? 1 : amplitude >= 1 ? 2 : 3
  return valeur.toLocaleString('fr-FR', { maximumFractionDigits: decimales })
}

/** Ce qui s'affiche dans une cellule : jamais une valeur inventée. */
function affichage(cellule: Cellule): string {
  if (cellule.valeurTexte) return cellule.valeurTexte
  const valeur = cellule.valeurSi ?? cellule.valeurOriginale
  if (valeur === null) return '—'
  return `${cellule.comparateur ?? ''}${nombreLisible(valeur)}`
}

const COULEURS = ['#1a63c4', '#b45309', '#067647', '#b42318', '#7c3aed', '#0e7490']

export default function Biologie({ patientId }: { patientId: string }) {
  const [selection, setSelection] = useState<string[]>([])
  const [source, setSource] = useState<{ id: string; page: number | null; passage: string | null } | null>(
    null,
  )
  const [saisie, setSaisie] = useState<{ cellule: Cellule; libelle: string } | 'nouveau' | null>(null)
  const [periode, setPeriode] = useState<'tout' | '1an' | '3ans'>('tout')

  const { data, isLoading } = useQuery({
    queryKey: ['biologie', patientId],
    queryFn: () => api<Biologie>(`/api/patients/${patientId}/biologie`),
  })

  const alerteParDonnee = useMemo(() => {
    const index = new Map<string, { severite: string; libelle: string }>()
    for (const alerte of data?.alertes ?? []) {
      if (alerte.donneeId) index.set(alerte.donneeId, alerte)
    }
    return index
  }, [data])

  const colonnes = useMemo(() => {
    if (!data) return []
    if (periode === 'tout') return data.colonnes
    const limite = new Date()
    limite.setFullYear(limite.getFullYear() - (periode === '1an' ? 1 : 3))
    return data.colonnes.filter((c) => new Date(c.datePrelevement) >= limite)
  }, [data, periode])

  // Un graphique par paramètre : deux unités différentes ne doivent jamais
  // partager un axe, sans quoi la comparaison visuelle est trompeuse
  // (cahier des charges, section 10).
  const courbes = useMemo(() => {
    if (!data || selection.length === 0) return []
    return selection.map((cle) => {
      const ligne = data.lignes.find((l) => (l.code ?? l.libelle) === cle)
      let exclus = 0
      const points = colonnes
        .map((colonne) => {
          const cellule = ligne?.cellules[colonne.id]
          if (!cellule) return null
          if (cellule.valeurSi === null) {
            exclus += 1
            return null
          }
          return {
            // Deux prélèvements du même jour portent la même date : l'axe a
            // besoin d'une clé propre à chacun, sinon les points se décalent.
            cle: colonne.id,
            etiquette: f.dateCourte(colonne.datePrelevement),
            valeur: cellule.valeurSi,
            laboratoire: colonne.laboratoire,
          }
        })
        .filter((point): point is NonNullable<typeof point> => point !== null)
      return { cle, libelle: ligne?.libelle ?? cle, unite: ligne?.uniteCible ?? '', points, exclus }
    })
  }, [data, colonnes, selection])

  if (isLoading) return <Chargement />
  if (!data) return null

  const basculer = (cle: string) =>
    setSelection((actuelle) =>
      actuelle.includes(cle) ? actuelle.filter((c) => c !== cle) : [...actuelle, cle].slice(-6),
    )

  if (data.colonnes.length === 0) {
    return (
      <Carte
        titre="Biologie"
        icone={<FlaskConical className="h-[17px] w-[17px] text-bleu-500" />}
        actions={
          <button className="bouton-discret px-2" onClick={() => setSaisie('nouveau')} title="Saisir un résultat">
            <Plus className="h-4 w-4" />
          </button>
        }
      >
        <EtatVide
          texte="Aucun résultat validé. Les bilans arrivent par les documents : dépose un compte rendu de laboratoire, ou saisis un résultat à la main."
          action={
            <button className="bouton-doux mt-1" onClick={() => setSaisie('nouveau')}>
              <Plus className="h-4 w-4" />
              Saisir un résultat
            </button>
          }
        />
        {saisie === 'nouveau' && (
          <FormulaireResultat patientId={patientId} fermer={() => setSaisie(null)} />
        )}
      </Carte>
    )
  }

  // Les lignes rangées par famille, pour qu'un hémogramme reste groupé.
  const categories = [...new Set(data.lignes.map((l) => l.categorie))]

  return (
    <Carte
      titre="Biologie"
      icone={<FlaskConical className="h-[17px] w-[17px] text-bleu-500" />}
      compte={data.lignes.length}
      actions={
        <>
          <select
            className="champ w-[128px] py-1 text-[12.5px]"
            value={periode}
            onChange={(e) => setPeriode(e.target.value as typeof periode)}
          >
            <option value="tout">Tout</option>
            <option value="3ans">3 ans</option>
            <option value="1an">1 an</option>
          </select>
          <button className="bouton-discret px-2" onClick={() => setSaisie('nouveau')} title="Saisir un résultat">
            <Plus className="h-4 w-4" />
          </button>
        </>
      }
    >
      <p className="px-5 pb-2 text-[12.5px] text-brume">
        Les résultats sont présentés dans les unités de référence. La valeur d’origine reste
        consultable au survol de chaque cellule. Clique sur un paramètre pour le tracer.
      </p>

      {/* Le tableau déborde en largeur : il défile seul, la page non. */}
      <div className="overflow-x-auto px-5 pb-4">
        <table className="w-full border-separate border-spacing-0 text-[12.5px]">
          <thead>
            <tr>
              <th className="sticky left-0 z-10 bg-white pb-2 text-left font-medium text-brume">
                Paramètre
              </th>
              {colonnes.map((colonne) => (
                <th key={colonne.id} className="px-2 pb-2 text-center font-medium whitespace-nowrap">
                  <span className="block text-bleu-700">{f.dateCourte(colonne.datePrelevement)}</span>
                  <span className="block text-[11px] font-normal text-brume">
                    {colonne.laboratoire ?? 'laboratoire non précisé'}
                  </span>
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {categories.map((categorie) => (
              <Fragment key={categorie}>
                <tr>
                  <td
                    colSpan={colonnes.length + 1}
                    className="sticky left-0 bg-white pt-3 pb-1 text-[11px] font-medium tracking-wide text-brume uppercase"
                  >
                    {categorie}
                  </td>
                </tr>
                {data.lignes
                  .filter((ligne) => ligne.categorie === categorie)
                  .map((ligne) => {
                    const cle = ligne.code ?? ligne.libelle
                    const choisi = selection.includes(cle)
                    return (
                      <tr key={cle} className={choisi ? 'bg-bleu-50' : undefined}>
                        <td className="sticky left-0 z-10 bg-inherit py-1.5 pr-3 whitespace-nowrap">
                          <button
                            className="text-left hover:text-bleu-700"
                            onClick={() => basculer(cle)}
                            title="Tracer ce paramètre"
                          >
                            <span className={choisi ? 'font-medium text-bleu-700' : ''}>
                              {ligne.libelle}
                            </span>
                            <span className="ml-1.5 text-[11px] text-brume">{ligne.uniteCible}</span>
                            {!ligne.connu && (
                              <span className="ml-1.5 text-[11px] text-attention">
                                hors table
                              </span>
                            )}
                          </button>
                        </td>

                        {colonnes.map((colonne) => {
                          const cellule = ligne.cellules[colonne.id]
                          if (!cellule) {
                            return (
                              <td key={colonne.id} className="px-2 py-1.5 text-center text-brume">
                                ·
                              </td>
                            )
                          }
                          const alerte = alerteParDonnee.get(cellule.donneeId)
                          const titre = [
                            cellule.valeurOriginale !== null
                              ? `Sur le compte rendu : ${cellule.valeurOriginale} ${cellule.uniteOriginale ?? ''}`
                              : null,
                            cellule.conversionFacteur && cellule.conversionFacteur !== 1
                              ? `Conversion appliquée : × ${cellule.conversionFacteur}`
                              : null,
                            cellule.refBasse !== null || cellule.refHaute !== null
                              ? `Références : ${cellule.refBasse ?? '?'} – ${cellule.refHaute ?? '?'}`
                              : null,
                            cellule.conversionNote,
                            cellule.laboratoire,
                          ]
                            .filter(Boolean)
                            .join('\n')

                          return (
                            <td key={colonne.id} className="px-2 py-1.5 text-center">
                              <button
                                className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 ${
                                  cellule.anormal
                                    ? 'bg-attention-fond font-medium text-attention'
                                    : 'hover:bg-slate-100'
                                }`}
                                title={titre}
                                onClick={() =>
                                  cellule.documentId
                                    ? setSource({
                                        id: cellule.documentId,
                                        page: cellule.page,
                                        passage: cellule.passage,
                                      })
                                    : setSaisie({ cellule, libelle: ligne.libelle })
                                }
                              >
                                {affichage(cellule)}
                                {cellule.conversionIncertaine && (
                                  <TriangleAlert className="h-3 w-3 text-attention" />
                                )}
                                {alerte && <span className="h-1.5 w-1.5 rounded-full bg-critique" />}
                              </button>
                            </td>
                          )
                        })}
                      </tr>
                    )
                  })}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {selection.length > 0 && (
        <div className="border-t border-trait px-5 py-4">
          <div className="mb-2 flex items-center gap-2">
            <ChartLine className="h-4 w-4 text-bleu-500" />
            <span className="text-[13px] font-medium">Évolution</span>
            <button className="bouton-discret ml-auto px-2 py-1 text-[12px]" onClick={() => setSelection([])}>
              Tout retirer
            </button>
          </div>

          <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
            {courbes.map((courbe, index) => (
              <div key={courbe.cle}>
                <div className="mb-1 flex items-baseline gap-2">
                  <span
                    className="text-[12.5px] font-medium"
                    style={{ color: COULEURS[index % COULEURS.length] }}
                  >
                    {courbe.libelle}
                  </span>
                  <span className="text-[11.5px] text-brume">{courbe.unite}</span>
                </div>

                {courbe.points.length < 2 ? (
                  <p className="rounded-lg bg-slate-50 px-3 py-6 text-center text-[12.5px] text-brume">
                    {courbe.points.length === 0
                      ? 'Aucune valeur convertie : rien à tracer.'
                      : 'Un seul résultat : pas encore de courbe.'}
                  </p>
                ) : (
                  <ResponsiveContainer width="100%" height={200}>
                    <LineChart data={courbe.points} margin={{ top: 5, right: 10, bottom: 5, left: -12 }}>
                      <CartesianGrid stroke="#e7edf5" vertical={false} />
                      <XAxis
                        dataKey="cle"
                        tick={{ fontSize: 11, fill: '#7c8ba1' }}
                        tickFormatter={(cle) =>
                          courbe.points.find((p) => p.cle === cle)?.etiquette ?? ''
                        }
                      />
                      <YAxis tick={{ fontSize: 11, fill: '#7c8ba1' }} domain={['auto', 'auto']} />
                      <Tooltip
                        contentStyle={{ fontSize: 12, borderRadius: 10, border: '1px solid #e7edf5' }}
                        formatter={(valeur) => [`${valeur} ${courbe.unite}`, courbe.libelle]}
                        labelFormatter={(_cle, charge) => {
                          const point = charge?.[0]?.payload as
                            | { etiquette: string; laboratoire: string | null }
                            | undefined
                          if (!point) return ''
                          return point.laboratoire
                            ? `${point.etiquette} — ${point.laboratoire}`
                            : point.etiquette
                        }}
                      />
                      <Line
                        type="monotone"
                        dataKey="valeur"
                        stroke={COULEURS[index % COULEURS.length]}
                        strokeWidth={2}
                        dot={{ r: 3 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                )}

                {courbe.exclus > 0 && (
                  <p className="mt-1 text-[11.5px] text-attention">
                    {courbe.exclus} résultat{courbe.exclus > 1 ? 's' : ''} sans conversion :
                    non tracé{courbe.exclus > 1 ? 's' : ''}, pour ne pas relier des valeurs qui ne
                    se comparent pas.
                  </p>
                )}
              </div>
            ))}
          </div>

        </div>
      )}

      {data.lignes.some((l) => !l.connu) && (
        <div className="px-5 pb-5">
          <Message ton="info">
            Certains paramètres ne figurent pas dans la table de conversions : ils sont conservés
            tels qu’écrits, sans conversion. La table est consultable dans les paramètres.
          </Message>
        </div>
      )}

      {source && (
        <VisionneuseDocument
          documentId={source.id}
          page={source.page}
          passage={source.passage}
          fermer={() => setSource(null)}
        />
      )}

      {saisie === 'nouveau' && (
        <FormulaireResultat patientId={patientId} fermer={() => setSaisie(null)} />
      )}
      {saisie && saisie !== 'nouveau' && (
        <FormulaireResultat
          patientId={patientId}
          resultat={saisie.cellule}
          libelle={saisie.libelle}
          fermer={() => setSaisie(null)}
        />
      )}
    </Carte>
  )
}
